import { useMemo, useCallback, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import useAuthStore from '@entities/auth/model/useAuthStore';

import { useGetPcApprovalDocListQuery } from '@features/approval/hooks';
import { getFilterFields } from '@features/approval/components/drafter/config/filterFields';
import { tableColumns } from '@features/approval/components/drafter/config/tableColumns';
import useApprovalSection from '@features/approval/hooks/useApprovalSection';
import { useDocFormListSimpleQuery } from '@features/form-selection/hooks';
import useGetTeamQuery from '@features/team/hooks';

import createExcelFile from '@shared/lib/excel';
import { formatDate, getDefaultDateRange } from '@shared/utils/formatDate';
import { useIsMobile } from '@shared/hooks';
import ContentSectionLayout from '@shared/components/organisms/ContentSectionLayout/ContentSectionLayout';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import { MobileLayout } from '@shared/components/organisms/mobile';
import Alert from '@shared/components/atoms/Alert';
import useApprovalCountStore from '@features/approval/model/useApprovalCountStore';

import styles from '@pages/approval-box/ApproverPage.module.scss';
import DocumentDetailModal from '@pages/approval-box/components/DocumentDetailModal/DocumentDetailModal';
import { MobileApproverContent, MobileDocumentDetail } from '@pages/approval-box/components/mobile';

function ApproverPage() {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const companyCode = useAuthStore((state) => state.companyCode);

  const [detailDocId, setDetailDocId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('approvalIng');

  /* =======================
   * 기본 조회 기간 (3개월)
   * ======================= */
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  /* =======================
   * 🔑 searchParams (탭별)
   * ======================= */
  const [approvalIngSearchParams, setApprovalIngSearchParams] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
  });

  const [futureIngSearchParams, setFutureIngSearchParams] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
  });

  const [doneSearchParams, setDoneSearchParams] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
  });

  /* =======================
   * 양식 / 팀 옵션
   * ======================= */
  const { data: docFormList = [] } = useDocFormListSimpleQuery(companyCode);

  const formOptions = useMemo(() => {
    return [
      { label: '전체', value: '' },
      ...docFormList.map((item) => ({
        label: item.title,
        value: item.doc,
      })),
    ];
  }, [docFormList]);

  const { data: teamResponse } = useGetTeamQuery(companyCode);

  const teamOption = useMemo(() => {
    const teamList = Array.isArray(teamResponse?.resultData?.teamList)
      ? teamResponse.resultData.teamList
      : [];

    return [
      { label: '전체', value: '' },
      ...teamList.map((item) => ({
        label: item.team,
        value: item.teamCode,
      })),
    ];
  }, [teamResponse]);

  /* =======================
   * 필터 필드 확장
   * ======================= */
  const getExtendedFilterFields = useCallback(
    (filterValues, currentFormOptions) => {
      const baseFields = getFilterFields(filterValues, currentFormOptions);
      const docTypeIndex = baseFields.findIndex((f) => f.key === 'docType');

      return [
        ...baseFields.slice(0, docTypeIndex + 1),
        {
          key: 'team',
          type: 'select',
          label: '기안부서',
          value: filterValues.team ?? '',
          options: teamOption,
        },
        ...baseFields.slice(docTypeIndex + 1),
      ];
    },
    [teamOption],
  );

  /* =======================
   * Excel Formatter
   * ======================= */
  const excelFormatter = useCallback(
    (rows) =>
      rows.map((r) => ({
        ...r,
        date: formatDate(r.date),
        corpSealUsed: r.is_corp_seal_used === 1 || r.is_corp_seal_used === '1' ? 'Y' : 'N',
      })),
    [],
  );

  /* =======================
   * useApprovalSection
   * ======================= */
  const approvalIngSection = useApprovalSection({
    queryType: 'approvalIng',
    searchParams: approvalIngSearchParams,
    setSearchParams: setApprovalIngSearchParams,
    filterFieldBuilder: getExtendedFilterFields,
    tableColumns,
    useQueryHook: useGetPcApprovalDocListQuery,
    formOptions,
    excelFormatter,
    defaultSort: { field: 'date', direction: 'desc' },
    isActiveTab: activeTab === 'approvalIng',
  });

  const futureIngSection = useApprovalSection({
    queryType: 'futureIng',
    searchParams: futureIngSearchParams,
    setSearchParams: setFutureIngSearchParams,
    filterFieldBuilder: getExtendedFilterFields,
    tableColumns,
    useQueryHook: useGetPcApprovalDocListQuery,
    formOptions,
    excelFormatter,
    defaultSort: { field: 'date', direction: 'desc' },
    isActiveTab: activeTab === 'futureIng',
  });

  const doneSection = useApprovalSection({
    queryType: 'done',
    searchParams: doneSearchParams,
    setSearchParams: setDoneSearchParams,
    filterFieldBuilder: getExtendedFilterFields,
    tableColumns,
    useQueryHook: useGetPcApprovalDocListQuery,
    formOptions,
    excelFormatter,
    defaultSort: { field: 'date', direction: 'desc' },
    isActiveTab: activeTab === 'done',
  });

  // 2026.02.23 엑셀용 컬럼 생성
  const excelColumns = useMemo(() => {
    return [
      ...tableColumns,

      {
        key: 'corpSealUsed',
        label: '법인인감사용',
        render: (_, row) =>
          row.is_corp_seal_used === 1 || row.is_corp_seal_used === '1' ? 'Y' : 'N',
      },
    ];
  }, [tableColumns]);
  /* =======================
   * 미결함 결재중 카운트 (state 숫자/문자열 모두 처리)
   * ======================= */
  const approvalIngCount = useMemo(() => {
    if (approvalIngSection.isLoading) return null;
    const rows = approvalIngSection.tableData || [];
    return rows.filter((row) => {
      const state = row.state;
      const stateCode = Number(row.stateCode ?? row.state);
      return state === '결재중' || state === 2 || state === '2' || stateCode === 2;
    }).length;
  }, [approvalIngSection.tableData, approvalIngSection.isLoading]);

  const { setApprovalIngCount } = useApprovalCountStore();

  useEffect(() => {
    if (approvalIngSection.isLoading) {
      setApprovalIngCount(null);
      return;
    }
    setApprovalIngCount(approvalIngCount);
  }, [approvalIngCount, approvalIngSection.isLoading, setApprovalIngCount]);

  /* =======================
   * 기타 UI 상태
   * ======================= */

  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
    mode: 'alert',
    onConfirm: null,
  });

  function showAlert({ message, mode = 'alert', onConfirm = null }) {
    setAlertState({ isOpen: true, message, mode, onConfirm });
  }

  function closeAlert() {
    setAlertState((prev) => ({ ...prev, isOpen: false }));
  }

  /* =======================
   * 행 클릭
   * ======================= */
  const handleRowClick = useCallback((row) => {
    const docId = row.idx || row.docIdx || row.document_idx;
    if (!docId) return;

    setDetailDocId(docId);
    setIsDetailModalOpen(true);
  }, []);

  const closeDetailModal = useCallback(
    async function () {
      setIsDetailModalOpen(false);
      setDetailDocId(null);
      // 목록 갱신 (invalidate + refetch로 캐시 유지하며 최신 데이터 반영, reset 대신 사용해 깜빡임 완화)
      queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
      queryClient.invalidateQueries({ queryKey: ['approvalList'] });
      await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
      await queryClient.refetchQueries({ queryKey: ['approvalList'] });
    },
    [queryClient],
  );

  /* =======================
   * 모바일
   * ======================= */
  if (isMobile) {
    return (
      <MobileLayout title="결재문서">
        <MobileApproverContent
          activeTab={activeTab}
          onTabChange={setActiveTab}
          approvalIngSection={approvalIngSection}
          futureIngSection={futureIngSection}
          doneSection={doneSection}
          onItemClick={handleRowClick}
          formOptions={formOptions}
          teamOptions={teamOption}
          approvalIngCount={approvalIngCount}
          approvalIngSectionLoading={approvalIngSection.isLoading}
        />

        <MobileDocumentDetail
          isOpen={isDetailModalOpen}
          onClose={closeDetailModal}
          docIdx={detailDocId}
          userRole="approver"
          tabType={activeTab}
        />

        <Alert
          isOpen={alertState.isOpen}
          message={alertState.message}
          mode={alertState.mode}
          onConfirm={alertState.onConfirm}
          onClose={closeAlert}
        />
      </MobileLayout>
    );
  }

  /* =======================
   * PC
   * ======================= */
  return (
    <MainLayout>
      <TabContentLayout
        lazyLoad
        tabs={[
          {
            value: 'approvalIng',
            label: approvalIngSection.isLoading ? '미결함' : `미결함 (${approvalIngCount})`,
            content: (
              <ContentSectionLayout
                {...approvalIngSection}
                classes={styles}
                showExcelButton
                emptyMessage="현재 미결 문서가 없습니다."
                onExcel={() =>
                  createExcelFile({
                    rows: approvalIngSection.handleExcel(),
                    fileName: '미결함.xlsx',
                    columns: excelColumns,
                  })
                }
                onRowClick={handleRowClick}
              />
            ),
          },
          {
            value: 'futureIng',
            label: '예결함',
            content: (
              <ContentSectionLayout
                {...futureIngSection}
                classes={styles}
                showExcelButton
                emptyMessage="현재 예결 문서가 없습니다."
                onExcel={() =>
                  createExcelFile({
                    rows: futureIngSection.handleExcel(),
                    fileName: '예결함.xlsx',
                    columns: excelColumns,
                  })
                }
                onRowClick={handleRowClick}
              />
            ),
          },
          {
            value: 'done',
            label: '기결함',
            content: (
              <ContentSectionLayout
                {...doneSection}
                classes={styles}
                showExcelButton
                emptyMessage="현재 기결 문서가 없습니다."
                onExcel={() =>
                  createExcelFile({
                    rows: doneSection.handleExcel(),
                    fileName: '기결함.xlsx',
                    columns: excelColumns,
                  })
                }
                onRowClick={handleRowClick}
              />
            ),
          },
        ]}
        defaultTab="approvalIng"
        onTabChange={setActiveTab}
      />

      <DocumentDetailModal
        isOpen={isDetailModalOpen}
        onClose={closeDetailModal}
        docIdx={detailDocId}
        userRole="approver"
        tabType={activeTab}
      />

      <Alert
        isOpen={alertState.isOpen}
        message={alertState.message}
        mode={alertState.mode}
        onConfirm={alertState.onConfirm}
        onClose={closeAlert}
      />
    </MainLayout>
  );
}

export default ApproverPage;
